import { Tooltip, Typography } from '@mui/material'

import PromptImage from '../TableCellComponents/PromptImage'
import GradingMethodSelector from '../TableCellComponents/GradingMethodSelector'
import { EvaluationCriteria } from '../TableCellComponents/EvaluationCriteria'
import LeniencySelector from '../TableCellComponents/LeniencySelector'
import MCGradingMethodSelector from '../TableCellComponents/MCGradingMethodSelector'
import ChoiceSelector from '../TableCellComponents/ChoiceSelector'
import CorrectChoicesViewer from '../TableCellComponents/CorrectChoicesViewer'
import FeedbackTypeViewer from '../TableCellComponents/FeedbackTypeViewer'
import RepeatTranslation from '../TableCellComponents/RepeatTranslation'
import { InlinePopoverEditor } from '../TableCellComponents/InlinePopoverEditor'
import CorrectAnswer from '../TableCellComponents/CorrectAnswer'

import { type ColumnDefinition } from './types'

import langs from '@/assets/langs.json'
import { CardActivityType } from '@/models/card-activity/card-activity.model'
import PointsAdjust from '@/components/PointsAdjust'
import VerificationStatus from '@/components/VerificationStatus'
import FeedbackLanguageSelector from '@/components/FeedbackLanguageSelector'

export const columns: ColumnDefinition[] = [
  {
    id: 'target_text',
    label: 'Target Text',
    types: [CardActivityType.READ_REPEAT],
    renderCell: ({ row, handleUpdateCard }) => <PromptImage card={row} updateCard={handleUpdateCard} />,
  },
  {
    id: 'verificationStatus',
    label: 'Verification Status',
    types: [CardActivityType.READ_REPEAT],
    renderCell: ({ row, handleUpdateCard }) => (
      <VerificationStatus
        card={row}
        handleUpdateTargetText={targetText => {
          handleUpdateCard(row.id, 'target_text', targetText)
        }}
        handleVerifyTerm={(verificationStatus, checked) => {
          handleUpdateCard(row.id, 'verificationStatus', verificationStatus)
          handleUpdateCard(row.id, 'checked', checked)
        }}
      />
    ),
  },
  {
    id: 'native_text',
    label: 'Translation',
    types: [CardActivityType.READ_REPEAT],
    renderCell: ({ row, handleUpdateCard }) => <RepeatTranslation card={row} updateCard={handleUpdateCard} />,
  },
  {
    id: 'repeat',
    label: 'Repeat Times',
    types: [CardActivityType.READ_REPEAT],
    renderCell: ({ row, handleUpdateCard }) => (
      <PointsAdjust
        customWidth="30px"
        points={row?.repeat ?? 1}
        // @ts-ignore
        setPoints={repeatTimes => {
          handleUpdateCard(row.id, 'repeat', repeatTimes)
        }}
      />
    ),
  },
  {
    id: 'difficulty',
    label: 'Leniency',
    types: [CardActivityType.READ_REPEAT],
    renderCell: ({ row, handleUpdateCard }) => <LeniencySelector card={row} updateCard={handleUpdateCard} />,
  },
  {
    id: 'language',
    label: 'Target Language',
    types: [CardActivityType.READ_REPEAT],
    renderCell: ({ row }) => (
      <Typography color="#475467" lineHeight="18px">
        {langs[row.language]}
      </Typography>
    ),
  },

  {
    id: 'prompt',
    label: 'Prompt',
    types: [CardActivityType.READ_RESPOND, CardActivityType.RESPOND_WRITE, CardActivityType.SHORT_ANSWER],
    renderCell: ({ row, handleUpdateCard }) => <PromptImage card={row} updateCard={handleUpdateCard} />,
  },
  {
    id: 'correct_answer',
    label: 'Correct Answer',
    types: [CardActivityType.SHORT_ANSWER],
    renderCell: ({ row, handleUpdateCard }) => <CorrectAnswer card={row} updateCard={handleUpdateCard} />,
  },
  {
    id: 'grading_method',
    label: 'Grading Method',
    types: [CardActivityType.READ_RESPOND, CardActivityType.RESPOND_WRITE],
    renderCell: ({ row, handleUpdateCard }) => <GradingMethodSelector card={row} updateCard={handleUpdateCard} />,
  },
  {
    id: 'grading_criteria',
    label: 'Evaluation Criteria',
    types: [CardActivityType.READ_RESPOND, CardActivityType.RESPOND_WRITE],
    renderCell: ({ row, handleUpdateCard }) => <EvaluationCriteria card={row} updateCard={handleUpdateCard} />,
  },
  {
    id: 'feedback_types',
    label: 'Feedback',
    types: [CardActivityType.READ_RESPOND, CardActivityType.RESPOND_WRITE],
    renderCell: ({ row }) => {
      return <FeedbackTypeViewer feedbackTypes={row?.feedback_types || []} />
    },
  },
  {
    id: 'feedback_language',
    label: 'Feedback Language',
    types: [CardActivityType.READ_RESPOND, CardActivityType.RESPOND_WRITE, CardActivityType.SHORT_ANSWER],
    renderCell: ({ row, handleUpdateCard }) => (
      <FeedbackLanguageSelector
        inputStyles={{
          borderColor: 'gray',
          '& .MuiInputBase-root': {
            height: 40,
            background: '#FFF',
            width: '165px',
          },
          '& .MuiSelect-outlined': {
            padding: '5px 14px',
          },
        }}
        label={null}
        targetLanguage={row?.language}
        value={row?.feedback_language || 'native'}
        onChange={value => {
          handleUpdateCard(row.id, 'feedback_language', value)
        }}
      />
    ),
  },

  {
    id: 'question',
    label: 'Question',
    types: [CardActivityType.MULTIPLE_CHOICE],
    renderCell: ({ row, handleUpdateCard }) => <PromptImage card={row} updateCard={handleUpdateCard} />,
  },
  {
    id: 'answer',
    label: 'Correct Answer',
    types: [CardActivityType.MULTIPLE_CHOICE],
    renderCell: ({ row }) => <CorrectChoicesViewer card={row} />,
  },
  {
    id: 'choices',
    label: 'Choices',
    types: [CardActivityType.MULTIPLE_CHOICE],
    renderCell: ({ row, handleUpdateCard }) => {
      return <ChoiceSelector card={row} updateCard={handleUpdateCard} />
    },
  },
  {
    id: 'MCQType',
    label: 'Grading Method',
    types: [CardActivityType.MULTIPLE_CHOICE],
    renderCell: ({ row, handleUpdateCard }) => <MCGradingMethodSelector card={row} updateCard={handleUpdateCard} />,
  },

  {
    id: 'points',
    label: 'Points',
    types: [
      CardActivityType.READ_REPEAT,
      CardActivityType.READ_RESPOND,
      CardActivityType.RESPOND_WRITE,
      CardActivityType.MULTIPLE_CHOICE,
      CardActivityType.SHORT_ANSWER,
    ],
    renderCell: ({ row, handleUpdateCard }) => (
      <PointsAdjust
        customWidth="30px"
        points={row?.points ?? 1}
        // @ts-ignore
        setPoints={points => {
          handleUpdateCard(row.id, 'points', points)
        }}
      />
    ),
  },
]
